import { Font } from '@react-pdf/renderer';
import styled, { css } from 'styled-components';
Font.register({
  family: 'Roboto',
  src: 'http://fonts.gstatic.com/s/roboto/v16/zN7GBFwfMP4uA6AR0HCoLQ.ttf',
});

export const AssetPercentiles = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 110px;
  align-self: center;
  margin-bottom: 30px;
`;

export const AssetPercentilesTitle = styled.span`
  font-size: 12px;
  color: '#212632';
`;

export const BarGraph = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 2%;
  align-items: flex-end;
  justify-content: center;
  position: relative;
`;

type BarProps = {
  height: number;
  highlighted: string;
};

export const Bar = styled.div<BarProps>`
  width: 60px;
  margin: 0;
  border-radius: 8px 8px 0 0;
  height: ${(props) => `${props.height}%`};
  ${(props) =>
    props.highlighted === 'true'
      ? css`
          background-color: #775175;
        `
      : css`
          background-color: #b6bbc6;
        `}
  background-color: ${(props) => props.color};
  z-index: 2;
`;

export const XAxis = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  left: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  font-size: 8px;
  color: #212632;
  z-index: 1;
  border-bottom: 2px solid #ccc;
`;

export const XAxisLabel = styled.span`
  width: 10%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
`;

export const YAxis = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  font-size: 8px;
  color: #212632;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

export const YAxisLabel = styled.span`
  width: 100%;
  position: absolute;
  display: flex;
  align-items: center;
  color: #747d93;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: 14px;
`;

export const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ccc;
  opacity: 0.4;
  margin-left: 30px;
  padding-left: 30px;
  z-index: 1;
`;

export const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  font-size: 10px;
`;

export const MiddleMark = styled.div`
  position: absolute;
  width: 3px;
  height: 100%;
  background-color: #cca009;
  left: 55%;
  z-index: 5;
`;
export const MiddleLabel = styled.div`
  position: absolute;
  left: 50%;
  height: 100%;
  top: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const MiddleLabelText = styled.span`
  position: absolute;
  top: -20px;
  color: #cca009;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

export const MinLabel = styled.span`
  position: absolute;
  text-align: center;
  left: 60px;
  bottom: -30px;
  color: #212632;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
`;
export const MaxLabel = styled.span`
  position: absolute;
  text-align: center;
  right: 20px;
  bottom: -30px;
  color: #212632;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

export const MedianContainer = styled.div`
  position: absolute;
  bottom: -30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: 15px;
  z-index: 15;
`;

export const MedianLegend = styled.div`
  color: #af8904;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

export const MedianPercentileLegend = styled.span`
  background-color: #fbeec3;
  padding: 8px;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  color: #af8904;
  font-size: 17px;
  font-weight: 500;
`;

type AssetValueMarkProps = {
  left: number;
  isOutOfRange?: boolean;
  isBelowRange?: boolean;
};

export const AssetValueMark = styled.div<AssetValueMarkProps>`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(props) =>
    props.left > 100 || props.left < 0 ? 'flex-end' : 'center'};
  row-gap: 3px;
  z-index: 10;
  left: ${(props) => {
    if (props.left > 100) return '100%';
    if (props.isBelowRange) return '2%';
    return `${props.left}%`;
  }};
  transform: ${(props) => {
    if (props.left > 100) return 'translateX(-100%)';
    if (props.left < 0) return 'none';
    return 'none';
  }};
`;

export const AssetInfoWrapper = styled.div<{
  isOutOfRange?: boolean;
  isBelowRange?: boolean;
}>`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: -60px;

  ${(props) =>
    props.isOutOfRange &&
    css`
      right: 10px;
      text-align: right;
    `}
  ${(props) =>
    props.isBelowRange &&
    css`
      left: 10px;
      text-align: left;
    `}
  ${(props) =>
    !props.isOutOfRange &&
    !props.isBelowRange &&
    css`
      left: -45px;
      text-align: center;
    `}
`;

export const AssetInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AssetValueLabel = styled.span<{
  isOutOfRange?: boolean;
  isBelowRange?: boolean;
}>`
  white-space: nowrap;
  font-family: 'Roboto', sans-serif;
  color: #212632;
  font-size: 14px;
  font-weight: 400;
`;

export const AssetValuePriceLabel = styled.span<{
  isOutOfRange?: boolean;
  isBelowRange?: boolean;
}>`
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #000;
`;

export const AssetValueLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ccc;
  opacity: 0.4;
  margin-left: 30px;
`;

export const Dot = styled.div<{ color: string }>`
  width: 2px;
  height: 4px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  display: flex;
  align-self: flex-start;
  justify-self: flex-start;
`;

export const OutlierContainer = styled.div<{
  isOutOfRange?: boolean;
  isBelowRange?: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.isOutOfRange &&
    css`
      text-align: right;
      align-items: flex-end;
    `}
  ${(props) =>
    props.isBelowRange &&
    css`
      text-align: left;
      align-items: flex-start;
    `}
`;

export const OutlierLabel = styled.span`
  text-align: right;
  display: flex;
  align-items: center;
  column-gap: 5px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: #943939;
  svg {
    width: 16px;
  }
`;
